import {Component, effect, inject, signal} from '@angular/core';
import {Location, NgClass} from "@angular/common";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {LoRaGateway, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {FormsModule} from "@angular/forms";
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {
  DeviceIdInputComponent,
  DeviceIdSelected
} from "src/app/presentation/common/device-id-input/device-id-input.component";
import {NavBarStateStore} from 'src/app/presentation/main-view/nav-bar/nav-bar-state/nav-bar-state.store';
import {Subject, takeUntil} from 'rxjs';
import {AddGatewayStore} from 'src/app/presentation/main-view/add-device/add-gateway/add-gateway.store';
import {
  dropAPinMessage,
  InternalNotification,
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {GatewayService} from 'src/app/domain/devices/gateway/gateway.service';
import {Router} from '@angular/router';
import {SpinnerComponent} from "src/app/presentation/common/spinner/spinner.component";
import {parseOptionalFloat} from 'src/app/domain/util/parsing/parsing';

@Component({
  selector: 'app-add-gateway',
  imports: [
    GenericButtonComponent,
    NgClass,
    FormsModule,
    DeviceIdInputComponent,
    SpinnerComponent
],
  templateUrl: './add-gateway.component.html',
  styleUrl: './add-gateway.component.css'
})
export class AddGatewayComponent {

  creatingGateway = signal(false);
  searchingForDeviceId = signal(false);
  validId = signal(false);

  canProcessNewGateway = signal(false);

  private $destroyDropAPin: Subject<void> = new Subject<void>();
  private dropAPinNotification?: InternalNotification;

  store = inject(AddGatewayStore);

  private farmStore = inject(FarmStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private menuStore = inject(NavBarStateStore);
  private router = inject(Router);

  constructor(
    private dataSubscriptionService: DataSubscriptionService,
    private dataSynchronisationService: DataSynchronisationService,
    private gatewayService: GatewayService,
    protected viewportService: ViewportService
  ) {
    effect(() => {
      this.canProcessNewGateway.set(
        !this.searchingForDeviceId() &&
        !this.creatingGateway() &&
        this.farmStore.farm() !== undefined &&
        this.store.name() !== undefined &&
        this.store.lat() !== undefined && this.store.long() !== undefined
      );
    });
  }

  back(): void {
    this.location.back();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }

  processDeviceId(deviceIdSelected: DeviceIdSelected) {
    const id = deviceIdSelected.id;
    if (!id) return;

    const farmId = this.farmStore.farm()?.id;
    if (!farmId) return;

    this.searchingForDeviceId.set(true);

    const parts = id.split(',');
    const deviceId = parts[parts.length - 1];

    this.store.setDeviceId(deviceId);
    this.gatewayService.get(deviceId).subscribe({
      next: gateway => {
        if (!gateway) {
          this.initializeForm();
        } else if (gateway.farmId === farmId) {
          this.router.navigate(['/main/customize-gateway', gateway.id]);
          this.reset();
        } else if (gateway.farmId === undefined) {
          this.store.setMatchesExistingDevice(true);
          this.store.setVersion(gateway.version);
          this.initializeForm(gateway);
        } else {
          this.internalNotificationsStore.addNotification(
            `LoRa gateway '${deviceId}' is assigned to another farm.`,
            InternalNotificationType.ERROR,
            false
          );
          this.reset();
        }

        this.searchingForDeviceId.set(false);
      },
      error: (errorResponse) => {
        const status = errorResponse.error?.status;
        if (status === 401) {
          this.internalNotificationsStore.addNotification(
            `LoRa gateway '${deviceId}' already exists and cannot be modified.`,
            InternalNotificationType.ERROR,
            false
          );
          this.reset();
        } else if (status === 400) {
          this.internalNotificationsStore.addNotification(
            `'${deviceId}' is not a valid LoRa gateway identifier.`,
            InternalNotificationType.ERROR,
            false
          );
          this.reset();
        } else {
          this.internalNotificationsStore.addNotification(
            `Unable to verify existince of LoRa gateway '${deviceId}'.`,
            InternalNotificationType.ERROR,
            false
          );
          this.reset();
        }

        this.searchingForDeviceId.set(false);
      }
    });
  }

  async processNewGateway() {
    if (!this.canProcessNewGateway()) return;

    this.creatingGateway.set(true);

    const gateway: LoRaGateway = {
      id: this.store.deviceId()!,
      name: this.store.name(),
      location: {
        coords: [this.store.lat()!, this.store.long()!]
      },
      version: this.store.version(),
      farmId: this.farmStore.farm()!.id,
      synchronizationStatus: SynchronizationStatus.syncPending
    }

    if (this.store.matchesExistingDevice()) {
      this.dataSynchronisationService.applyAction(
        DataType.loRaGateways,
        ActionType.mergeAsset,
        gateway,
        SynchronizationStatus.syncPending
      );
    } else {
      this.dataSynchronisationService.applyAction(
        DataType.loRaGateways,
        ActionType.createAsset,
        gateway,
        SynchronizationStatus.creationPending
      );
    }

    this.router.navigate(['/main/information-center']);

    this.reset();
  }

  setName(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setName(string === '' ? undefined : string);
  }

  setLat(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLat(parseOptionalFloat(string));
  }

  setLong(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLong(parseOptionalFloat(string));
  }

  private initializeForm(gateway?: LoRaGateway) {
    if (gateway) {
      this.initializeFormWithGateway(gateway!);
    } else {
      this.initializeBlankForm();
    }
  }

  private initializeFormWithGateway(gateway: LoRaGateway) {
    this.store.setName(gateway.name);
    if (gateway.location?.coords) {
      this.store.setLat(gateway.location.coords[0]);
      this.store.setLong(gateway.location.coords[1]);
    }
  }

  private initializeBlankForm() {
    const farm = this.farmStore.farm();
    if (!farm || !farm.location || !farm.location.coords || !farm.location.coords[0] || !farm.location.coords[1]) return;

    this.store.setLat(farm.location.coords[0]);
    this.store.setLong(farm.location.coords[1]);
  }

  reset() {
    this.creatingGateway.set(false);
    this.searchingForDeviceId.set(false);
    this.store.reset();
  }

  onDropAPin() {
    this.menuStore.disable();
    this.dropAPinNotification = this.internalNotificationsStore.addNotification(dropAPinMessage, InternalNotificationType.INFO, true);
    this.dataSubscriptionService.publish('dropAPin', true)
    this.dataSubscriptionService.onFirst<{
      lng: number,
      lat: number
    }>('currentPosition').pipe(takeUntil(this.$destroyDropAPin)).subscribe(data => {
      if (confirm('Are you sure you want to use this location for this LoRa gateway?')) {
        this.store.setLat(data.lat);
        this.store.setLong(data.lng);
      }
      this.menuStore.enable();
      this.$destroyDropAPin.complete();
      this.$destroyDropAPin = new Subject<void>();
      this.dataSubscriptionService.publish('currentPosition');
      if (this.dropAPinNotification) this.internalNotificationsStore.deleteNotification(this.dropAPinNotification);
      this.dataSubscriptionService.publish('dropAPin', false);
      this.mainViewStore.showChildComponent();
    });
    this.mainViewStore.hideChildComponent();
  }
}
